/**
 * useGlossaryShortcut · slot_vocabulary_navbar VN.7 (17 MAY 2026 · Genie ack «a»).
 *
 * Registers a global Cmd/Ctrl+K keydown listener that toggles the App-owned
 * GlossaryOverlay open/close state. Extracted from App.tsx as a hook for
 * isolated unit testing without mounting the full Router tree.
 *
 * Guards:
 *   - skipped when `screen` is in HIDDEN_NAV_SCREENS (login/welcome/finaltest/
 *     auth · per Sidebar canonical set) — глоссарий не доступен в immersive
 *     auth/exam flows.
 *   - skipped when keydown target is an input/textarea/contentEditable
 *     element — не hijack typing.
 *   - EXCEPTION: if the target is the GlossaryOverlay's own search input
 *     (matched via closest `[role="dialog"].glossary-drawer`), the shortcut
 *     still fires so power users can re-press Cmd+K to close the drawer from
 *     within search field.
 *
 * Behavior: toggle (open if closed · close if open). Callback-form setState
 * keeps the effect deps churn к [screen] only (zero re-bind on glossaryOpen
 * mutations).
 *
 * Cross-platform: accepts BOTH metaKey (Cmd · Mac) and ctrlKey (Ctrl ·
 * Win/Linux) regardless of detected platform. The visual hint badge в
 * GlossaryOverlay header shows the platform-canonical label.
 *
 * Layout-independent: matches `e.code === 'KeyK'` (physical key position),
 * not `e.key` (rendered character — would be 'л' on a Cyrillic layout and
 * miss the shortcut entirely).
 */

import { useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { HIDDEN_NAV_SCREENS } from '@/components/Sidebar';

export interface UseGlossaryShortcutArgs {
  /** Current screen · used to gate the listener via HIDDEN_NAV_SCREENS. */
  screen: string;
  /** React setState (or callback-compatible) для GlossaryOverlay open flag. */
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export function useGlossaryShortcut({ screen, setOpen }: UseGlossaryShortcutArgs): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (HIDDEN_NAV_SCREENS.has(screen)) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      // Match the PHYSICAL key (layout-independent). e.key would be 'л' on
      // a Cyrillic layout and miss the shortcut. e.code is always 'KeyK'.
      if (e.code !== 'KeyK') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const isEditable =
        tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable === true;
      // Allow re-toggle from drawer's own search input (closes the drawer).
      const isGlossarySearch =
        target instanceof HTMLInputElement &&
        target.closest('[role="dialog"]')?.classList.contains('glossary-drawer') === true;
      if (isEditable && !isGlossarySearch) return;
      e.preventDefault();
      setOpen((prev) => !prev);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [screen, setOpen]);
}
